import React, {useCallback, useState, Fragment} from 'react';
import {useDispatch} from 'react-redux';
import {relatedDocumentsTab} from '../../../util/uiconstants';
import {labels} from '../../../util/utils';
import {raiseJSValidationError} from '../../../actions/erroractions';
import EllipsesControl from '../EllipsesControl/EllipsesControl';
import Icon from '../BaseComponentWrappers/MotifIcon';
import MotifDropdown from '@ey/motif-react-wrapper/Dropdown';
import MotifDropdownItem from '@ey/motif-react-wrapper/DropdownItem';
import Button from '../BaseComponentWrappers/MotifButton';
import MotifButton from '@ey/motif-react-wrapper/Button';
import {IconoirPlusCircle} from '@ey-xd/motif-icon';

export default function AddDocumentButtons({
	activeTab,
	setShowALRAPackageModal,
	uploadRef,
	relateExistingDocuments,
	groupInstructionDrawerEvidence,
	isGroupRiskAssessmentSection,
	relatedTasks,
	taskId,
	isUploadDisabled = false,
	isDisabled
}) {
	const dispatch = useDispatch();
	const [isOpen, setIsOpen] = useState(false);

	const _isDisabled = isUploadDisabled || isDisabled;

	const handleClickOutside = useCallback(() => {
		setIsOpen(false);
	}, []);

	const onAddDocumentClick = useCallback(() => {
		setIsOpen(!isOpen);
	}, [isOpen]);

	const handleCreateALRAPackageClick = () => {
		setShowALRAPackageModal(true);
	};

	const handleOnClickGroupInstructiondRawer = () => {
		setIsOpen(false);

		if (taskId) {
			uploadRef.current?.onUploadClick();
		} else {
			dispatch(raiseJSValidationError(labels.noTaskRelatedToThisDocument));
		}
	};

	const handleRelateDocumentClick = useCallback(() => {
		setIsOpen(false);
		relateExistingDocuments();
	}, [relateExistingDocuments]);

	const handleOnClick = useCallback(() => {
		setIsOpen(false);
		uploadRef.current?.onUploadClick();
	}, [uploadRef]);

	const renderAddDocumentslink = () => {
		if (activeTab === relatedDocumentsTab.EVIDENCE || activeTab === relatedDocumentsTab.TEMPORARY) {
			return (
				<MotifDropdown
					open={isOpen}
					handleClickOutside={handleClickOutside}
					trigger={
						<MotifButton
							id="add-document-trigger"
							variant="text-alt"
							onClick={onAddDocumentClick}
							disabled={
								_isDisabled ||
								(activeTab === relatedDocumentsTab.TEMPORARY && (isUploadDisabled || relatedTasks?.length === 0))
							}
						>
							<Icon src={IconoirPlusCircle} />
							<span>{activeTab === relatedDocumentsTab.TEMPORARY ? labels.addTemporaryFiles : labels.addDocument}</span>
						</MotifButton>
					}
				>
					{activeTab === relatedDocumentsTab.TEMPORARY ? renderAddTemporaryFilesDropDownItems() : renderDropDownItems()}
				</MotifDropdown>
			);
		} else if (activeTab === relatedDocumentsTab.PRIOR_PERIOD) {
			return (
				<section className="addDocumentWrapper">
					<section className="addEvidenceContainer">
						<Icon src={IconoirPlusCircle} onClick={relateExistingDocuments} />
						<Button
							variant="text-alt"
							className="addPriorPeriodEvidenceButton"
							name="relateDocument"
							value={labels.relateDocument}
							onClick={relateExistingDocuments}
							showLabel
							buttonLabel={labels.addPriorPeriodEvidence}
							disabled={_isDisabled}
						/>
					</section>
				</section>
			);
		} else if (activeTab === relatedDocumentsTab.EXTERNAL_DOCUMENTS) {
			return (
				<section className="addDocumentWrapper">
					<MotifDropdown
						open={isOpen}
						handleClickOutside={handleClickOutside}
						trigger={
							<MotifButton
								id="add-document-trigger"
								variant="text-alt"
								onClick={onAddDocumentClick}
								disabled={relatedTasks?.length === 0 || _isDisabled}
							>
								<Icon src={IconoirPlusCircle} />
								<span>{labels.addExternalDocuments}</span>
							</MotifButton>
						}
					>
						<MotifDropdownItem onClick={handleRelateDocumentClick}>
							<EllipsesControl noOfLines={1} content={labels.relateFiles} tooltip={labels.relateFiles} />
						</MotifDropdownItem>
					</MotifDropdown>
				</section>
			);
		}
	};

	const renderDropDownItems = () => {
		return (
			<Fragment>
				{groupInstructionDrawerEvidence && (
					<MotifDropdownItem onClick={handleOnClickGroupInstructiondRawer}>
						<EllipsesControl noOfLines={1} content={labels.uploadDocument} tooltip={labels.uploadDocument} />
					</MotifDropdownItem>
				)}
				{!groupInstructionDrawerEvidence && (
					<MotifDropdownItem onClick={handleOnClick} disabled={isUploadDisabled}>
						<EllipsesControl noOfLines={1} content={labels.uploadDocument} tooltip={labels.relateDocument} />
					</MotifDropdownItem>
				)}
				<MotifDropdownItem onClick={handleRelateDocumentClick}>
					<EllipsesControl noOfLines={1} content={labels.relateDocument} tooltip={labels.relateDocument} />
				</MotifDropdownItem>
				{groupInstructionDrawerEvidence && isGroupRiskAssessmentSection && (
					<MotifDropdownItem onClick={handleCreateALRAPackageClick}>
						<EllipsesControl
							noOfLines={1}
							content={labels.generateAccountRiskAssessmentPackage}
							tooltip={labels.generateAccountRiskAssessmentPackage}
						/>
					</MotifDropdownItem>
				)}
			</Fragment>
		);
	};

	const renderAddTemporaryFilesDropDownItems = () => {
		return (
			<Fragment>
				<MotifDropdownItem onClick={handleRelateDocumentClick}>
					<EllipsesControl noOfLines={1} content={labels.relateFiles} tooltip={labels.relateDocument} />
				</MotifDropdownItem>
				{/*File upload isn't allowed in group audit feature for 2.11 */}
				{!groupInstructionDrawerEvidence && (
					<MotifDropdownItem onClick={handleOnClick}>
						<EllipsesControl noOfLines={1} content={labels.uploadFiles} tooltip={labels.relateDocument} />
					</MotifDropdownItem>
				)}
			</Fragment>
		);
	};

	return <>{renderAddDocumentslink()}</>;
}
